import { useAuth } from '@/hooks/useAuth';
import { ProfileEditCard } from '@/components/ProfileEditCard';
import { PasswordChangeCard } from '@/components/PasswordChangeCard';
import { PlanInfoCard } from '@/components/PlanInfoCard';
import { CodeRedemptionCard } from '@/components/CodeRedemptionCard';
import { CodeUsageHistoryCard } from '@/components/CodeUsageHistoryCard';
import { Copy, Gift } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export const ProfilePage = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const hasAttemptedGeneration = useRef(false);

  // 招待コードが空の場合、自動生成する（1回のみ）
  useEffect(() => {
    const generateInvitationCode = async () => {
      // 既に生成を試みた場合はスキップ
      if (hasAttemptedGeneration.current) return;
      
      // ユーザーが存在し、招待コードが空で、生成中でない場合のみ実行
      if (user?.uid && !user.invitationCode && !isGenerating) {
        hasAttemptedGeneration.current = true;
        setIsGenerating(true);
        
        try {
          // 招待コードを生成（8文字の英数字）
          const generateCode = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let code = '';
            for (let i = 0; i < 8; i++) {
              code += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return code;
          };

          const newCode = generateCode();

          // Firestoreに保存
          const userRef = doc(db, 'users', user.uid);
          await updateDoc(userRef, {
            invitationCode: newCode,
          });

          console.log('招待コードを生成しました:', newCode);
        } catch (error) {
          console.error('招待コード生成エラー:', error);
          // エラーが発生した場合、再試行を許可
          hasAttemptedGeneration.current = false;
        } finally {
          setIsGenerating(false);
        }
      }
    };

    generateInvitationCode();
  }, [user?.uid, user?.invitationCode]); // user の変更だけを監視

  const handleCopyInvitationCode = () => {
    if (user?.invitationCode) {
      navigator.clipboard.writeText(user.invitationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* ページタイトル */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-indigo-800 bg-clip-text text-transparent">
          プロフィール設定
        </h1>
        <p className="text-gray-600 mt-2">アカウント情報の管理</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左カラム */}
        <div className="space-y-6">
          {/* プロフィール編集 */}
          <ProfileEditCard />

          {/* 招待コード */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Gift className="w-6 h-6 text-orange-600" />
              招待コード
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              友達を招待して特典を獲得しましょう
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={user?.invitationCode || (isGenerating ? '生成中...' : '読み込み中...')}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-center text-lg font-semibold"
              />
              <button
                onClick={handleCopyInvitationCode}
                disabled={!user?.invitationCode}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'コピー済み' : 'コピー'}
              </button>
            </div>
          </div>
        </div>

        {/* 右カラム */}
        <div className="space-y-6">
          {/* パスワード変更 */}
          <PasswordChangeCard />

          {/* プラン情報 */}
          <PlanInfoCard />
          
          {/* コード利用 */}
          {user && <CodeRedemptionCard userId={user.uid} onSuccess={() => window.location.reload()} />}
          
          {/* コード利用履歴 */}
          {user && <CodeUsageHistoryCard userId={user.uid} />}
        </div>
      </div>
    </div>
  );
};
